/**
 * pi-article-search – custom element
 * Page field is UI-only trigger; click opens modal. Real search input and results live inside the modal.
 */
(function () {
  const root = (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';

  function debounce(fn, ms) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  function escapeHtml(s) {
    if (s == null) return '';
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  class PiArticleSearch extends HTMLElement {
    constructor() {
      super();
      this._abortController = null;
      this._boundCloseBackdrop = this._onBackdropClick.bind(this);
      this._boundKeydown = this._onKeydown.bind(this);
    }

    connectedCallback() {
      this._trigger = this.querySelector('.pi-search__trigger');
      this._overlay = this.querySelector('.pi-search__overlay');
      this._input = this.querySelector('.pi-search__input');
      this._results = this.querySelector('.pi-search__results');
      if (!this._trigger || !this._overlay || !this._input || !this._results) return;

      this._trigger.addEventListener('click', this._onTriggerClick.bind(this));
      this._trigger.addEventListener('focus', this._onTriggerFocus.bind(this));
      this._input.addEventListener('input', debounce(this._onInput.bind(this), 280));
      this._overlay.addEventListener('click', this._boundCloseBackdrop);
      document.addEventListener('keydown', this._boundKeydown);
    }

    disconnectedCallback() {
      this._overlay.removeEventListener('click', this._boundCloseBackdrop);
      document.removeEventListener('keydown', this._boundKeydown);
      this._abortController?.abort();
    }

    _onTriggerClick(ev) {
      ev.preventDefault();
      this._openModal();
    }

    _onTriggerFocus(ev) {
      ev.preventDefault();
      this._openModal();
    }

    _openModal() {
      this._showOverlay();
      this._setEmptyState();
      this._input.value = '';
      this._input.focus();
    }

    _onInput(ev) {
      const q = (ev.target.value || '').trim();
      if (q.length === 0) {
        this._setEmptyState();
        return;
      }
      this._runSearch(q);
    }

    _onBackdropClick(ev) {
      if (ev.target !== this._overlay) return;
      this._hideOverlay();
    }

    _onKeydown(ev) {
      if (ev.key !== 'Escape') return;
      if (this._overlay.classList.contains('is-open')) {
        this._hideOverlay();
        this._input.blur();
        this._trigger.focus();
      }
    }

    _hideOverlay() {
      this._overlay.classList.remove('is-open');
      this._overlay.setAttribute('aria-hidden', 'true');
    }

    _showOverlay() {
      this._overlay.classList.add('is-open');
      this._overlay.setAttribute('aria-hidden', 'false');
    }

    _setEmptyState() {
      this._results.innerHTML = '';
      const el = document.createElement('p');
      el.className = 'pi-search__empty';
      el.textContent = 'Type to search articles.';
      this._results.appendChild(el);
    }

    _setLoading(loading) {
      this._results.innerHTML = '';
      if (loading) {
        const el = document.createElement('p');
        el.className = 'pi-search__loading';
        el.textContent = 'Searching…';
        this._results.appendChild(el);
      }
      this._showOverlay();
    }

    _setResults(articles) {
      this._results.innerHTML = '';
      if (!articles || articles.length === 0) {
        const el = document.createElement('p');
        el.className = 'pi-search__empty';
        el.textContent = 'No articles found.';
        this._results.appendChild(el);
        this._showOverlay();
        return;
      }
      articles.forEach((article) => {
        const a = document.createElement('a');
        a.href = article.url || '#';
        a.className = 'pi-search__card';
        const imgSize = getComputedStyle(this).getPropertyValue('--pi-search-card-image-size').trim() || '56px';
        const imageUrl = article.image || '';
        const tag = Array.isArray(article.tags) && article.tags.length > 0 ? article.tags[0] : '';
        a.innerHTML =
          '<span class="pi-search__card-image">' +
          (imageUrl
            ? '<img src="' + escapeHtml(imageUrl) + '" alt="" width="' + imgSize.replace('px', '') + '" height="' + imgSize.replace('px', '') + '" loading="lazy">'
            : '') +
          '</span>' +
          '<span class="pi-search__card-body">' +
          '<span class="pi-search__card-title">' + escapeHtml(article.title || '') + '</span>' +
          (tag ? '<span class="pi-search__card-tag">' + escapeHtml(tag) + '</span>' : '') +
          '</span>';
        this._results.appendChild(a);
      });
      this._showOverlay();
    }

    async _runSearch(q) {
      this._abortController?.abort();
      this._abortController = new AbortController();
      this._setLoading(true);

      const params = new URLSearchParams({
        q: q,
        'resources[type]': 'article',
        'resources[limit]': '10',
        'resources[limit_scope]': 'each',
        'resources[options][fields]': 'title,body,author,tag'
      });
      const url = root + 'search/suggest.json?' + params.toString();

      try {
        const res = await fetch(url, { signal: this._abortController.signal });
        const data = await res.json();
        const articles = (data.resources && data.resources.results && data.resources.results.articles) || [];
        this._setResults(articles);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this._setResults([]);
      }
    }
  }

  if (!window.customElements.get('pi-article-search')) {
    window.customElements.define('pi-article-search', PiArticleSearch);
  }
})();
